/* ============================================================
   AVORA HOME — Cart AOV recommendations
   Contextual products are ranked against the exact paid-subtotal gap
   to the next Avora reward. Prices always come from Shopify.
   ============================================================ */

(function () {
  'use strict';

  var recommendationCache = {};
  var currentCart = null;
  var updateSequence = 0;
  var quickAddPending = false;
  var quickAddPromise = Promise.resolve();
  var discoveryProducts = [];
  var discoveryPage = 1;
  var discoveryLimit = 24;
  var discoveryHasMore = true;
  var discoveryLoading = false;
  var discoveryStarted = false;
  var discoveryError = false;
  var discoveryFocusIndex = null;

  function getPlanner() {
    return window.AvoraCartUpsellCore;
  }

  function getRoutesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCatalogElement() {
    return document.getElementById('avora-cart-upsell-catalog');
  }

  function getSaleSettings() {
    var element = getCatalogElement();
    return {
      active: !!(element && element.dataset.saleActive === 'true'),
      percentage: element ? Number(element.dataset.salePercentage) || 0 : 0
    };
  }

  function getFallbackCatalog() {
    var element = getCatalogElement();
    if (!element) return [];

    try {
      return JSON.parse(element.textContent || '[]');
    } catch (_) {
      return [];
    }
  }

  function getRewardTiers() {
    var planner = getPlanner();
    if (window.__avoraRewardsLocalizedConfig && Array.isArray(window.__avoraRewardsLocalizedConfig.tiers)) {
      return window.__avoraRewardsLocalizedConfig.tiers;
    }
    var configElement = document.getElementById('avora-rewards-config');
    if (configElement) {
      try {
        var parsed = JSON.parse(configElement.textContent || '{}');
        if (parsed && Array.isArray(parsed.tiers) && parsed.tiers.length) return parsed.tiers;
      } catch (_) {}
    }
    if (!planner) return [];
    var localizer = window.AvoraMarketLocalization;
    var marketContext = localizer && localizer.context(window);
    return marketContext && marketContext.ready
      ? planner.DEFAULT_TIERS.map(function (tier) { return localizer.localizeTier(tier, marketContext); })
      : [];
  }

  function formatMoney(cents, omitZeroDecimals) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || window.__shopCurrencyCode || 'EUR';
    if (window.AvoraMarketLocalization) return window.AvoraMarketLocalization.formatMinor(cents, currency, document.documentElement.lang || 'en', omitZeroDecimals);
    var amount = Number(cents) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: omitZeroDecimals && Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: omitZeroDecimals && Number.isInteger(amount) ? 0 : 2
      }).format(amount);
    } catch (_) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function getProductImage(product, variant) {
    var image = variant && (variant.featured_image || variant.featuredImage || variant.image);
    image = image || product.image || product.featured_image || product.featuredImage;
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  }

  function getProductUrl(product) {
    return product.url || (product.handle ? getRoutesRoot() + 'products/' + product.handle : '#');
  }

  function toCents(value) {
    var number = Number(value);
    return Number.isFinite(number) ? Math.round(number) : 0;
  }

  function normalizeProduct(product, rank) {
    if (!product) return null;

    var variants = Array.isArray(product.variants) ? product.variants : [];
    var availableVariants = variants.filter(function (variant) { return variant && variant.available !== false; });
    availableVariants.sort(function (a, b) { return toCents(a.price) - toCents(b.price); });
    var variant = availableVariants[0] || variants[0] || null;
    var hasOnlyDefaultVariant = product.hasOnlyDefaultVariant === true || product.has_only_default_variant === true || variants.length === 1;
    var rawPrice = product.effective === true
      ? toCents(product.price)
      : toCents((variant && variant.price) || product.price_min || product.price);
    var compareAt = product.effective === true
      ? toCents(product.compareAt || product.compare_at)
      : toCents((variant && variant.compare_at_price) || product.compare_at_price_min || product.compare_at_price);
    var sale = getSaleSettings();

    // A compare-at price means Shopify's price is already the sale price.
    if (product.effective !== true && sale.active && sale.percentage > 0 && rawPrice > 0 && !(compareAt > rawPrice)) {
      compareAt = rawPrice;
      rawPrice = Math.round(rawPrice * (100 - sale.percentage) / 100);
    }

    var id = Number(product.id || product.product_id);
    var variantId = Number(product.variantId || product.variant_id || (variant && variant.id));
    var variantTitle = product.variantTitle || product.variant_title || (variant && variant.title) || '';
    var quantityMin = Math.max(1, Number(
      product.quantityMin || product.quantity_min ||
      (variant && variant.quantity_rule && variant.quantity_rule.min) || 1
    ));
    var available = product.available !== false && (!variant || variant.available !== false);

    if (!id || !variantId || !rawPrice || !available) return null;

    return {
      id: id,
      variantId: variantId,
      variantTitle: variantTitle,
      quantityMin: quantityMin,
      title: product.title || '',
      url: getProductUrl(product),
      image: getProductImage(product, variant),
      price: rawPrice,
      compareAt: compareAt > rawPrice ? compareAt : 0,
      hasOnlyDefaultVariant: hasOnlyDefaultVariant,
      rank: Number(rank) || 0
    };
  }

  function mergeCandidates(recommended, fallback) {
    var seen = {};
    var merged = [];

    (recommended || []).concat(fallback || []).forEach(function (product, index) {
      var normalized = normalizeProduct(product, index);
      if (!normalized || seen[normalized.id]) return;
      seen[normalized.id] = true;
      merged.push(normalized);
    });

    return merged;
  }

  function fetchRecommendations(cart) {
    var planner = getPlanner();
    var firstItem = cart && cart.items && cart.items.find(function (item) {
      return !(planner && planner.isRewardGift(item));
    });
    var productId = firstItem && firstItem.product_id;
    var fallback = getFallbackCatalog();

    if (!productId) return Promise.resolve(mergeCandidates([], fallback));
    if (recommendationCache[productId]) {
      return Promise.resolve(mergeCandidates(recommendationCache[productId], fallback));
    }

    var url = getRoutesRoot() + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=10&intent=related';
    return fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Recommendations request failed');
        return response.json();
      })
      .then(function (data) {
        recommendationCache[productId] = (data && data.products) || [];
        return mergeCandidates(recommendationCache[productId], fallback);
      })
      .catch(function () {
        return mergeCandidates([], fallback);
      });
  }

  function rankCandidates(products, cart) {
    var planner = getPlanner();
    // Wait for localized reward terms before making a monetary promise.
    if (!planner || !window.__avoraRewardsLocalizedConfig) {
      return { products: products.slice(0, 6), nextTier: null, gap: 0, mode: 'loading', pairTotal: 0 };
    }
    // Rank already-priced catalogue items against the authoritative current reward gap.
    // Never extrapolate a second percentage discount onto the candidate prices.
    var rewardState=window.AvoraRewards && window.AvoraRewards.getState();
    var qualified=rewardState && Number.isFinite(rewardState.paidSubtotal) ? rewardState.paidSubtotal : planner.paidSubtotal(cart);
    var planningCart={items:(cart.items||[]).map(function(item){return Object.assign({},item,{original_line_price:0,final_line_price:0});})};
    planningCart.items.push({product_id:0,quantity:1,original_line_price:qualified,final_line_price:qualified,properties:{}});
    var plan=planner.recommendationPlan(products,planningCart,getRewardTiers(),6);
    plan.estimated=true; // Added-item discounts/eligibility are confirmed by Shopify after adding.
    return plan;
  }

  function escapeHTML(value) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(value || '')));
    return div.innerHTML;
  }

  function escapeAttribute(value) {
    return escapeHTML(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/`/g, '&#96;');
  }

  function ensureRootStructure(root) {
    if (root.querySelector('[data-avora-upsell-list]')) return;
    root.innerHTML =
      '<div class="avora-cart-upsell__header">' +
        '<div>' +
          '<h3 class="avora-cart-upsell__title" data-avora-upsell-title></h3>' +
          '<div class="avora-cart-upsell__offer" data-avora-upsell-offer hidden></div>' +
          '<p class="avora-cart-upsell__intro" data-avora-upsell-intro></p>' +
        '</div>' +
      '</div>' +
      '<div class="avora-cart-upsell__list" data-avora-upsell-list></div>' +
      '<p class="avora-cart-upsell__status" data-avora-upsell-status role="status" aria-live="polite"></p>';
  }

  function ensureDrawerRoot() {
    [['[data-avora-reward-suggestions]','reward'],['[data-avora-general-suggestions]','general']].forEach(function(target){
      var host=document.querySelector(target[0]);if(!host||host.querySelector('[data-avora-cart-upsell]'))return;
      var root=document.createElement('section');root.className='avora-cart-upsell avora-cart-upsell--drawer';
      root.dataset.avoraCartUpsell='';root.dataset.surface='drawer';root.dataset.recommendationKind=target[1];root.hidden=true;
      root.setAttribute('aria-label',target[1]==='reward'?'Suggestions toward your next reward':'You might also like');host.appendChild(root);
    });
  }

  function getRootCopy(surface, plan) {
    if (plan.mode === 'loading') {
      return { title: window.AvoraI18n.text('You might also like'), intro: window.AvoraI18n.text('Your rewards are loading.') };
    }
    if (plan.mode === 'discounted') {
      return { title: window.AvoraI18n.text('You might also like'), intro: window.AvoraI18n.text('Add an item to see your updated rewards after discounts.') };
    }
    if (!plan.nextTier) {
      return { title: window.AvoraI18n.text('You’ve unlocked all rewards'), intro: window.AvoraI18n.text('A few more finds for your home.') };
    }

    var title = window.AvoraI18n.text('Spend another {amount} to get', {amount: formatMoney(plan.gap, true)});
    return {
      title: title,
      intro: window.AvoraI18n.text('Choose what you love. Rewards are recalculated after adding.')
    };
  }

  function renderRewardOffer(plan) {
    if (!plan.nextTier) return '';
    var tier = plan.nextTier;
    var config = window.__avoraRewardsLocalizedConfig || {};
    var currentTier = getRewardTiers().reduce(function (current, candidate) {
      return candidate.threshold <= plan.paidSubtotal && candidate.threshold >= current.threshold ? candidate : current;
    }, { threshold: 0, wallet: 0, futureCard: 0 });
    var currentWallet = Number(currentTier.wallet) || 0;
    var giftIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9M12 8v13M12 8C3 8 5 1 8 3c2 1 4 5 4 5s2-4 4-5c3-2 5 5-4 5Z"/></svg>';
    var cardIcon = '<svg viewBox="0 0 40 28" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><rect x="1" y="1" width="38" height="26" rx="4" fill="#efe0c6"/><path d="M26 1v26M30 1v26M7 19h10M7 22h6"/><path d="M28 11c-9-7-11 4 0 1 11 3 9-8 0-1Z" fill="#d0ae7d"/></svg>';
    var art = config.giftCutoutImage
      ? '<img src="' + escapeAttribute(config.giftCutoutImage) + '" alt="" width="64" height="64" loading="lazy" decoding="async">'
      : giftIcon;
    var walletDetail = currentWallet
      ? window.AvoraI18n.text('{amount} more for gifts', {amount: formatMoney(Math.max(0, tier.wallet - currentWallet), true)})
      : window.AvoraI18n.text('Choose gifts you love');
    var html = '<div class="avora-cart-upsell__rewards' + (tier.futureCard ? ' has-future' : '') + '">' +
      '<div class="avora-cart-upsell__reward avora-cart-upsell__reward--wallet">' +
        window.AvoraI18n.fragment('<span class="avora-cart-upsell__reward-when">With this order</span>') +
        '<strong class="avora-cart-upsell__reward-value" data-avora-upsell-wallet>' + escapeHTML(formatMoney(tier.wallet, true)) + '</strong>' +
        window.AvoraI18n.fragment('<span class="avora-cart-upsell__reward-name">Gift Wallet</span>') +
        '<span class="avora-cart-upsell__reward-art">' + art + '</span>' +
        '<span class="avora-cart-upsell__reward-detail">' + escapeHTML(walletDetail) + '</span>' +
      '</div>';
    if (tier.futureCard) {
      html += window.AvoraI18n.fragment('<span class="avora-cart-upsell__reward-plus" role="img" aria-label="Plus">+</span>') +
        '<div class="avora-cart-upsell__reward avora-cart-upsell__reward--future">' +
        '<span class="avora-cart-upsell__reward-when">' + (currentTier.futureCard ? window.AvoraI18n.text('Your Reward Card grows to') : window.AvoraI18n.text('For a future order')) + '</span>' +
        '<strong class="avora-cart-upsell__reward-value" data-avora-upsell-card>' + escapeHTML(formatMoney(tier.futureCard, true)) + '</strong>' +
        window.AvoraI18n.fragment('<span class="avora-cart-upsell__reward-name">Reward Card</span>') +
        '<span class="avora-cart-upsell__reward-art">' + cardIcon + '</span>' +
        '<span class="avora-cart-upsell__reward-detail">' + (tier.futureMinimum ? escapeHTML(window.AvoraI18n.text('Off your next {amount}+ order', {amount: formatMoney(tier.futureMinimum, true)})) : window.AvoraI18n.text('For your next order')) + '</span>' +
      '</div>';
    }
    html += '</div>';
    if (tier.futureCard) {
      var delay = Number(config.returnWindowDays) || 0;
      html += '<p class="avora-cart-upsell__reward-terms">' +
        (delay ? escapeHTML(window.AvoraI18n.text('Reward Card emailed {days} days after your order is processed.', {days: delay})) : window.AvoraI18n.text('See reward details for delivery timing.')) +
      '</p>';
    }
    return html;
  }

  function getProductKicker(product, plan) {
    if (plan.estimated || !plan.nextTier || product.pairPath) return '';
    if (product.crossesNextTier) return window.AvoraI18n.text('Gets you the rewards');
    return window.AvoraI18n.text('Then spend {amount} more', {amount: formatMoney(product.remainingAfterAdd, true)});
  }

  function getActionOutcome(product, plan) {
    if (plan.estimated || !plan.nextTier) return '';
    var reward = formatMoney(plan.nextTier.wallet, true) + window.AvoraI18n.text(' Gift Wallet');
    if (product.crossesNextTier) return window.AvoraI18n.text(' to get a ') + reward;
    return window.AvoraI18n.text('; then spend ') + formatMoney(product.remainingAfterAdd, true) + window.AvoraI18n.text(' more to get a ') + reward;
  }

  function renderProduct(product, index, surface, plan) {
    var actionLabel = window.AvoraI18n.text('Add');
    var kicker = getProductKicker(product, plan);
    var actionOutcome = getActionOutcome(product, plan);
    var exactVariant = product.variantTitle && product.variantTitle !== 'Default Title' ? product.variantTitle : '';
    var actionAttributes = 'button type="button" data-surface="' + escapeAttribute(surface) + '" data-avora-upsell-add data-variant-id="' + product.variantId + '" data-quantity="' + product.quantityMin + '" data-product-title="' + escapeAttribute(product.title) + '" data-variant-title="' + escapeAttribute(exactVariant) + '" data-outcome="' + escapeAttribute(actionOutcome) + '"';
    var variantLabel = exactVariant
      ? '<span class="avora-cart-upsell__variant">' + escapeHTML(exactVariant) + '</span>'
      : '';
    var image = product.image
      ? '<img class="avora-cart-upsell__image" src="' + escapeAttribute(product.image) + '" alt="" loading="lazy" width="180" height="180">'
      : '';
    var compare = product.compareAt
      ? '<span class="avora-cart-upsell__compare">' + escapeHTML(formatMoney(product.compareAt, false)) + '</span>'
      : '';

    return '<article class="avora-cart-upsell__product" style="--avora-upsell-index:' + index + '">' +
      '<a class="avora-cart-upsell__image-link" href="' + escapeAttribute(product.url) + '" aria-label="' + escapeAttribute(product.title) + '">' + image + '</a>' +
      '<div class="avora-cart-upsell__details">' +
        (kicker ? '<span class="avora-cart-upsell__kicker">' + escapeHTML(kicker) + '</span>' : '') +
        '<a class="avora-cart-upsell__product-title" href="' + escapeAttribute(product.url) + '">' + escapeHTML(product.title) + '</a>' +
        '<div class="avora-cart-upsell__price-row">' +
          variantLabel + '<span class="avora-cart-upsell__price">' + escapeHTML(formatMoney(product.price, false)) + '</span>' + compare +
        '</div>' +
      '</div>' +
      '<' + actionAttributes + (quickAddPending ? ' disabled' : '') + ' class="avora-cart-upsell__action" aria-label="' + actionLabel + ' ' + escapeAttribute(product.title) + (exactVariant ? ', ' + escapeAttribute(exactVariant) : '') + escapeAttribute(actionOutcome) + '">' +
        '<span class="avora-cart-upsell__action-mark" aria-hidden="true">+</span>' +
        '<span class="avora-cart-upsell__action-label">' + actionLabel + '</span>' +
      '</button>' +
    '</article>';
  }

  function renderRoot(root, plan) {
    ensureRootStructure(root);

    var surface = root.dataset.surface || 'page';
    // Present a choice of individual products, keeping discovery below the cart.
    // Pair ranking may put the closest combination first; it is not a required bundle.
    var limit = root.dataset.recommendationKind === 'general' ? 6 : (surface === 'drawer' ? 4 : 6);
    var chosen = plan.products.slice(0, limit);
    if (!chosen.length) {
      root.hidden = true;
      return;
    }

    var copy = getRootCopy(surface, plan);
    if (root.dataset.recommendationKind === 'general') copy={title:window.AvoraI18n.text('You might also like'),intro:''};
    root.dataset.planMode = plan.mode;
    root.dataset.rewardGap = String(plan.gap || 0);
    var title = root.querySelector('[data-avora-upsell-title]');
    title.innerHTML = plan.nextTier
      ? escapeHTML(window.AvoraI18n.text('Spend another {amount} to get', {amount: 'AVORA_AMOUNT_SLOT'})).replace('AVORA_AMOUNT_SLOT', '<strong>' + escapeHTML(formatMoney(plan.gap, true)) + '</strong>')
      : escapeHTML(copy.title);
    var offer = root.querySelector('[data-avora-upsell-offer]');
    offer.innerHTML = plan.nextTier ? '<span>' + escapeHTML(formatMoney(plan.nextTier.wallet,true)) + ' '+window.AvoraI18n.text('Gift Wallet') + '</span>' + (plan.nextTier.futureCard ? '<small>+ '+escapeHTML(formatMoney(plan.nextTier.futureCard,true))+' '+window.AvoraI18n.text('Reward Card for a future order')+'</small>' : '') : '';
    offer.hidden = !plan.nextTier;
    root.querySelector('[data-avora-upsell-intro]').textContent = copy.intro;
    root.querySelector('[data-avora-upsell-list]').innerHTML = chosen.map(function (product, index) {
      return renderProduct(product, index, surface, plan);
    }).join('');
    root.hidden = false;
  }

  function discoveryImage(src, width) {
    if (!src) return '';
    try {
      var url = new URL(src, window.location.href);
      if (url.hostname === 'cdn.shopify.com' || /\/cdn\/shop\/(files|products)\//.test(url.pathname)) {
        url.protocol='https:';
        url.searchParams.set('width', width);
        url.searchParams.delete('height');
      }
      return url.href;
    } catch (_) {return src;}
  }

  function discoverySrcset(src) {
    return [360,540,720,1080].map(function(width) {return discoveryImage(src,width)+' '+width+'w';}).join(', ');
  }

  function discoveryGallery(product) {
    var sources = [product.image].concat(product.images || []);
    var seen = {};
    return sources.map(function(image) {
      return typeof image === 'string' ? image : image && (image.src || image.url);
    }).filter(function(src) {
      if (!src) return false;
      var key = discoveryImage(src,540);
      // Shopify serves the same asset through both the shop domain and CDN.
      // Ignore resize/version queries and the delivery host when deduplicating.
      try {
        var url = new URL(key,window.location.href);
        var asset = url.pathname.match(/^.*\/(files|products)\/(.+)$/);
        if (asset && (url.hostname === 'cdn.shopify.com' || /\/cdn\/shop\//.test(url.pathname))) key = asset[1]+'/'+asset[2];
      } catch (_) {}
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function discoveryProduct(product, index) {
    var html = renderProduct(product, index, 'page', {estimated:true});
    var images = product.images || [product.image];
    var selected = product.imageIndex || 0;
    var src = images[selected] || product.image;
    if (src) {
      var image = '<img class="avora-cart-upsell__image" src="'+escapeAttribute(discoveryImage(src,540))+'" srcset="'+escapeAttribute(discoverySrcset(src))+'" sizes="(max-width:599px) calc((100vw - 44px) / 2), (max-width:999px) 30vw, 23vw" alt="'+escapeAttribute(product.title)+'" loading="lazy" decoding="async" width="540" height="540" draggable="false">';
      html = html.replace(/<img class="avora-cart-upsell__image"[^>]*>/, image);
      var controls = '';
      if (images.length > 1) {
        controls = [-1,1].map(function(direction) {
          return '<button type="button" class="avora-carousel__arrow avora-cart-gallery__arrow avora-cart-gallery__arrow--'+(direction<0?'prev':'next')+'" data-discovery-image="'+direction+'" aria-label="'+escapeAttribute(window.AvoraI18n.text(direction<0?'Previous image':'Next image')+' — '+product.title)+'"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="'+(direction<0?'M15 18l-6-6 6-6':'M9 18l6-6-6-6')+'"/></svg></button>';
        }).join('')+'<span class="avora-cart-gallery__count" data-gallery-count aria-live="polite" aria-atomic="true">'+(selected+1)+' / '+images.length+'</span>';
      }
      html = html.replace(/<a class="avora-cart-upsell__image-link"[\s\S]*?<\/a>/,function(link) {
        return '<div class="avora-cart-gallery" data-discovery-gallery="'+product.id+'"'+(images.length>1?' data-gallery-multiple':'')+'>'+link+controls+'</div>';
      });
    }
    if (!product.hasOnlyDefaultVariant) {
      html = html.replace(/<button[^>]*data-avora-upsell-add[\s\S]*?<\/button>/, '<a class="avora-cart-upsell__action" href="'+escapeAttribute(product.url)+'">'+escapeHTML(window.AvoraI18n.text('Choose options'))+'</a>');
    }
    return html;
  }

  function galleryProduct(gallery) {
    return discoveryProducts.find(function(item) {return item.id===Number(gallery.dataset.discoveryGallery);});
  }

  // Like the homepage, prepare neighbouring photos before the arrow is used.
  // Cache per card and responsive size; never share loading state between cards.
  function prepareDiscoveryImage(gallery, product, index) {
    var width = Math.ceil(gallery.clientWidth * (window.devicePixelRatio || 1));
    var key = index + ':' + width;
    var cache = gallery._imageCache || (gallery._imageCache = {});
    if (cache[key]) return cache[key];
    var image = new Image();
    var entry = cache[key] = {image:image, ready:false};
    entry.promise = new Promise(function(resolve) {
      var settled = false;
      var timeout = window.setTimeout(function() {finish(false);},12000);
      function finish(ok) {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        image.onload=image.onerror=null;
        entry.ready=ok;
        if (!ok) delete cache[key];
        resolve(ok);
      }
      image.onload=function() {
        if (image.decode) image.decode().then(function(){finish(true);},function(){finish(image.naturalWidth>0);});
        else finish(true);
      };
      image.onerror=function() {finish(false);};
      image.sizes=gallery.querySelector('img').sizes;
      image.srcset=discoverySrcset(product.images[index]);
      image.src=discoveryImage(product.images[index],540);
    });
    return entry;
  }

  function warmDiscoveryGallery(gallery) {
    var product=galleryProduct(gallery);
    if(!product || product.images.length<2) return;
    var index=product.imageIndex || 0, count=product.images.length;
    prepareDiscoveryImage(gallery,product,(index+1)%count);
    prepareDiscoveryImage(gallery,product,(index+count-1)%count);
  }

  function changeDiscoveryImage(gallery, direction) {
    var product=galleryProduct(gallery);
    if (!product || product.images.length<2) return;
    var index=((gallery._pendingIndex != null ? gallery._pendingIndex : product.imageIndex || 0)+direction+product.images.length)%product.images.length;
    gallery._pendingIndex=index;
    var request=(gallery._imageRequest || 0)+1;
    gallery._imageRequest=request;
    var entry=prepareDiscoveryImage(gallery,product,index);
    function show(ok) {
      if(request!==gallery._imageRequest || !gallery.isConnected) return;
      gallery._pendingIndex=null;
      gallery.removeAttribute('aria-busy');
      if(!ok) return; // Keep the last successful photo on network failure.
      var image=gallery.querySelector('img');
      if (gallery._imageTransition) {gallery._imageTransition.cancel();gallery._imageTransition=null;}
      var old=gallery.querySelector('[data-gallery-outgoing]');
      if(old) old.remove();
      var animate=image.animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if(animate) {
        old=image.cloneNode();
        old.removeAttribute('id');old.alt='';old.setAttribute('aria-hidden','true');
        old.setAttribute('data-gallery-outgoing','');
        old.loading='eager';
        gallery.append(old);
      }
      image.loading='eager';
      image.srcset=entry.image.srcset;
      image.src=entry.image.src;
      if(animate) {
        // Fade the outgoing photo over the already decoded next photo. Inputs
        // remain live; a subsequent click replaces this transition immediately.
        var transition=old.animate([{opacity:1},{opacity:0}],{duration:180,easing:'cubic-bezier(.25,.1,.25,1)'});
        gallery._imageTransition=transition;
        transition.onfinish=function(){old.remove();if(gallery._imageTransition===transition)gallery._imageTransition=null;};
      }
      product.imageIndex=index;
      gallery.querySelector('[data-gallery-count]').textContent=(index+1)+' / '+product.images.length;
      warmDiscoveryGallery(gallery);
    }
    if(entry.ready) show(true); // Commit immediately; the crossfade never blocks input.
    else {gallery.setAttribute('aria-busy','true');entry.promise.then(show);}
  }

  var discoveryGalleryObserver;
  function observeDiscoveryGalleries(root) {
    if (!('IntersectionObserver' in window)) return;
    if (!discoveryGalleryObserver) discoveryGalleryObserver=new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if(!entry.isIntersecting) return;
        warmDiscoveryGallery(entry.target);
        discoveryGalleryObserver.unobserve(entry.target);
      });
    }, {rootMargin:'240px'});
    root.querySelectorAll('[data-gallery-multiple]:not([data-gallery-observed])').forEach(function(gallery) {
      gallery.dataset.galleryObserved='';
      discoveryGalleryObserver.observe(gallery);
    });
  }

  function bindDiscoveryGallery() {
    var gesture=null;
    ['pointerover','focusin'].forEach(function(type) {
      document.addEventListener(type,function(event) {
        var card=event.target.closest('.avora-cart-discovery .avora-cart-upsell__product');
        var gallery=card && card.querySelector('[data-gallery-multiple]');
        if(gallery) warmDiscoveryGallery(gallery);
      },{passive:true});
    });
    document.addEventListener('click',function(event) {
      var gallery=event.target.closest('[data-discovery-gallery]');
      if(!gallery) return;
      var arrow=event.target.closest('[data-discovery-image]');
      if(arrow) {
        event.preventDefault();event.stopPropagation();
        changeDiscoveryImage(gallery,Number(arrow.dataset.discoveryImage));
      } else if(Date.now()<(gallery._suppressClickUntil || 0)) {
        event.preventDefault();event.stopPropagation();
      }
    },true);
    document.addEventListener('keydown',function(event) {
      var gallery=event.target.closest('[data-gallery-multiple]');
      if(!gallery || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      changeDiscoveryImage(gallery,event.key==='ArrowLeft'?-1:1);
    });
    document.addEventListener('pointerdown',function(event) {
      var gallery=event.target.closest('[data-gallery-multiple]');
      if(!gallery || event.pointerType==='mouse' || event.target.closest('button')) return;
      gesture={gallery:gallery,id:event.pointerId,x:event.clientX,y:event.clientY};
    },{passive:true});
    document.addEventListener('pointerup',function(event) {
      if(!gesture || gesture.id!==event.pointerId) return;
      var current=gesture;gesture=null;
      var dx=event.clientX-current.x,dy=event.clientY-current.y;
      if(Math.abs(dx)<32 || Math.abs(dx)<Math.abs(dy)*1.3) return;
      current.gallery._suppressClickUntil=Date.now()+500;
      changeDiscoveryImage(current.gallery,dx<0?1:-1);
    },{passive:true});
    document.addEventListener('pointercancel',function(){gesture=null;},{passive:true});
  }

  function ensureDiscovery() {
    var host = document.querySelector('.cart-page__more-blocks');
    if (!host) return null;
    var root = host.querySelector('[data-avora-cart-discovery]');
    if (!root) {
      root = document.createElement('section');
      root.className = 'avora-cart-discovery avora-cart-upsell';
      root.dataset.avoraCartDiscovery = '';
      root.dataset.avoraCartUpsell = '';
      root.dataset.surface = 'page';
      root.dataset.recommendationKind = 'catalogue';
      root.setAttribute('aria-labelledby', 'avora-cart-discovery-title');
      root.innerHTML = '<header class="avora-cart-discovery__header"><h2 id="avora-cart-discovery-title">'+escapeHTML(window.AvoraI18n.text('Explore the collection'))+'</h2><a href="'+escapeAttribute(getRoutesRoot()+'collections/all')+'">'+escapeHTML(window.AvoraI18n.text('View all'))+' <span aria-hidden="true">→</span></a></header><div class="avora-cart-discovery__grid" data-discovery-grid></div><p class="avora-cart-upsell__status" data-avora-upsell-status role="status" aria-live="polite"></p><div class="avora-cart-discovery__footer"><p data-discovery-status role="status"></p><button type="button" data-discovery-more>'+escapeHTML(window.AvoraI18n.text('Show more'))+' <span aria-hidden="true">↓</span></button></div>';
      host.prepend(root);
      // Replace only the old four-product recommendation section, not merchant blocks.
      host.classList.add('has-avora-discovery');
      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function(entries) {
          if (!entries.some(function(entry) {return entry.isIntersecting;})) return;
          observer.disconnect();
          if (!discoveryStarted) loadDiscovery();
        }, {rootMargin:'500px'});
        observer.observe(root);
      } else if (!discoveryStarted) loadDiscovery();
    }
    renderDiscovery(root);
    return root;
  }

  function renderDiscovery(root) {
    root = root || document.querySelector('[data-avora-cart-discovery]');
    if (!root) return;
    var grid = root.querySelector('[data-discovery-grid]');
    var visible = discoveryProducts.slice(0,discoveryLimit);
    // Append rather than replace: keep scroll position, focused actions and decoded images.
    visible.slice(grid.children.length).forEach(function(product,index) {
      grid.insertAdjacentHTML('beforeend',discoveryProduct(product,grid.children.length));
    });
    observeDiscoveryGalleries(root);
    root.querySelectorAll('[data-avora-upsell-add]').forEach(function(button) {button.disabled=quickAddPending;});
    if (discoveryFocusIndex !== null && grid.children.length > discoveryFocusIndex) {
      var nextCard = grid.children[discoveryFocusIndex];
      discoveryFocusIndex = null;
      nextCard.querySelector('a').focus({preventScroll:true});
      nextCard.scrollIntoView({block:'nearest'});
    }
    var more = root.querySelector('[data-discovery-more]');
    more.hidden = !discoveryHasMore && discoveryLimit >= discoveryProducts.length;
    more.disabled = discoveryLoading;
    more.textContent = window.AvoraI18n.text(discoveryLoading ? 'Loading…' : discoveryError ? 'Try again' : 'Show more') + (discoveryLoading ? '' : ' ↓');
    root.querySelector('[data-discovery-status]').textContent = discoveryError ? window.AvoraI18n.text('Could not load products. Please try again.') : discoveryLoading ? window.AvoraI18n.text('Loading…') : '';
  }

  function loadDiscovery() {
    if (discoveryLoading || !discoveryHasMore) return;
    discoveryStarted = true;
    discoveryLoading = true;
    discoveryError = false;
    renderDiscovery();
    var controller = new AbortController();
    var timeout = window.setTimeout(function() {controller.abort();},15000);
    fetch(getRoutesRoot()+'products.json?limit=48&page='+discoveryPage, {credentials:'same-origin',signal:controller.signal,headers:{'X-Requested-With':'XMLHttpRequest'}})
      .then(function(response) {if(!response.ok) throw new Error('Catalogue unavailable');return response.json();})
      .then(function(data) {
        if (!Array.isArray(data.products)) throw new Error('Invalid catalogue');
        var before = discoveryProducts.length;
        data.products.forEach(function(product) {
          if (discoveryProducts.some(function(existing) {return existing.id===Number(product.id);})) return;
          // Product-list JSON uses major-unit prices; recommendations use minor units.
          var variants = (product.variants || []).map(function(variant) {
            return Object.assign({},variant,{price:Math.round(Number(variant.price)*100),compare_at_price:Math.round(Number(variant.compare_at_price)*100)});
          });
          var normalized = normalizeProduct(Object.assign({},product,{variants:variants,image:product.images && product.images[0]}),discoveryProducts.length);
          if (normalized) {
            var selected = variants.find(function(variant) {return Number(variant.id)===normalized.variantId;});
            var sale=getSaleSettings();
            // Match the Liquid sale arithmetic used by the storefront cards.
            if(selected && sale.active && sale.percentage>0 && !(selected.compare_at_price>selected.price)) normalized.price=selected.price-Math.floor(selected.price*sale.percentage/100);
            normalized.images=discoveryGallery({image:normalized.image,images:product.images});
            normalized.imageIndex=0;
            discoveryProducts.push(normalized);
          }
        });
        discoveryHasMore = data.products.length >= 48;
        // A repeated page must not leave an endless load-more action.
        if (discoveryPage > 1 && data.products.length && before===discoveryProducts.length && data.products.every(function(product) {return discoveryProducts.some(function(existing) {return existing.id===Number(product.id);});})) discoveryHasMore=false;
        discoveryPage += 1;
      })
      .catch(function() {discoveryError=true;})
      .finally(function() {window.clearTimeout(timeout);discoveryLoading=false;renderDiscovery();});
  }

  function renderAll(cart) {
    currentCart = cart;
    ensureDiscovery();
    if (!cart || !cart.item_count) {
      document.querySelectorAll('[data-avora-cart-upsell]:not([data-avora-cart-discovery])').forEach(function (root) { root.hidden = true; });
      return Promise.resolve();
    }

    ensureDrawerRoot();
    var sequence = ++updateSequence;
    return fetchRecommendations(cart).then(function (products) {
      if (sequence !== updateSequence) return;
      var plan = rankCandidates(products, cart);
      var chosenIds=plan.nextTier?plan.products.map(function(p){return p.id;}):[];
      var generalProducts=products.filter(function(p){return chosenIds.indexOf(p.id)===-1 && !(cart.items||[]).some(function(item){return item.product_id===p.id;});}).slice(0,6);
      document.querySelectorAll('[data-avora-cart-upsell]').forEach(function (root) {
        if(root.dataset.recommendationKind==='catalogue') return;
        if(root.dataset.recommendationKind==='general'){
          renderRoot(root,{products:generalProducts,nextTier:null,gap:0,mode:'general',pairTotal:0});
        }else if(plan.nextTier){renderRoot(root,plan);}
        else{root.hidden=true;}
      });
    });
  }

  function collectCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (component) {
      if (ids.indexOf(component.dataset.sectionId) === -1) ids.push(component.dataset.sectionId);
    });
    return ids;
  }

  function announce(button, message, isError) {
    var root = button.closest('[data-avora-cart-upsell]') || document.querySelector('[data-avora-cart-upsell][data-surface="' + (button.dataset.surface === 'drawer' ? 'drawer' : 'page') + '"]');
    if (root && root.hasAttribute('data-avora-cart-discovery')) {
      var card = button.closest('.avora-cart-upsell__product');
      var notice = card && card.querySelector('.avora-cart-discovery__notice');
      if (card && isError && !notice) {
        notice = document.createElement('p');
        notice.className = 'avora-cart-discovery__notice';
        notice.setAttribute('role','alert');
        card.append(notice);
      }
      if (notice) {notice.textContent=isError?message:'';notice.hidden=!isError;}
    }
    var status = root && root.querySelector('[data-avora-upsell-status]');
    if (status) {
      status.textContent = message;
      status.classList.toggle('is-error', !!isError);
    }
  }

  function quickAdd(button) {
    if (quickAddPending || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    var variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    var originalLabel = button.querySelector('.avora-cart-upsell__action-label');
    var originalText = originalLabel ? originalLabel.textContent : window.AvoraI18n.text('Add');
    var sectionIds = collectCartSectionIds();
    var quantity = Math.max(1, Number(button.dataset.quantity) || 1);
    var addSucceeded = false;
    quickAddPending = true;
    document.querySelectorAll('[data-avora-upsell-add]').forEach(function (action) { action.disabled = true; });
    button.setAttribute('aria-busy', 'true');
    if (originalLabel) originalLabel.textContent = 'Adding';

    var addPayload = {
      items: [{ id: variantId, quantity: quantity }],
      sections_url: window.location.pathname
    };
    if (sectionIds.length) addPayload.sections = sectionIds.join(',');
    var controller = new AbortController();
    var requestTimer = window.setTimeout(function () { controller.abort(); }, 12000);

    quickAddPromise = fetch(getRoutesRoot() + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(addPayload),
      signal: controller.signal
    })
      .then(function (response) {
        if (!response.ok) return response.json().then(function (error) { throw error; });
        return response.json();
      })
      .then(function (addResponse) {
        addSucceeded = true;
        return fetch(getRoutesRoot() + 'cart.js', {
          credentials: 'same-origin',
          signal: controller.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        }).then(function (response) {
          if (!response.ok) throw new Error('Cart refresh failed');
          return response.json();
        }).then(function (cart) {
          quickAddPending = false;
          button.classList.add('is-added');
          button.removeAttribute('aria-busy');
          if (originalLabel) originalLabel.textContent = window.AvoraI18n.text('Added');
          var addedName = button.dataset.productTitle || 'Product';
          if (button.dataset.variantTitle) addedName += ' (' + button.dataset.variantTitle + ')';
          announce(button, addedName + window.AvoraI18n.text(' added to your cart. Updating your rewards…'));
          if (button.closest('[data-avora-cart-discovery]')) window.setTimeout(function() {
            if (!button.isConnected || button.getAttribute('aria-busy') === 'true') return;
            button.classList.remove('is-added');
            if (originalLabel) originalLabel.textContent=window.AvoraI18n.text('Add');
          },2200);

          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              cart: cart,
              sourceId: 'avora-cart-upsell',
              data: {
                cart: cart,
                source: 'avora-cart-upsell',
                itemCount: cart.item_count,
                variantId: String(variantId),
                sections: addResponse.sections || {}
              }
            }
          }));

          if (window.avoraCartDrawer && typeof window.avoraCartDrawer.refresh === 'function') {
            window.avoraCartDrawer.refresh();
          }

          window.setTimeout(function () { renderAll(cart); }, 80);
        });
      })
      .catch(function (error) {
        quickAddPending = false;
        document.querySelectorAll('[data-avora-upsell-add]').forEach(function (action) { action.disabled = false; });
        button.removeAttribute('aria-busy');
        if (addSucceeded) {
          button.disabled = true;
          button.classList.add('is-added');
          if (originalLabel) originalLabel.textContent = window.AvoraI18n.text('Added');
          announce(button, (button.dataset.productTitle || 'Product') + window.AvoraI18n.text(' was added. Refreshing your cart totals.'));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }
        if (originalLabel) originalLabel.textContent = originalText;
        announce(button, (error && error.name !== 'AbortError' && (error.description || error.message)) || window.AvoraI18n.text('Could not add this item. Please try again.'), true);
      }).finally(function () { window.clearTimeout(requestTimer); });
  }

  function fetchCartAndRender() {
    return fetch(getRoutesRoot() + 'cart.js', {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      })
      .then(renderAll)
      .catch(function () {});
  }

  function init() {
    bindDiscoveryGallery();
    document.addEventListener('click', function (event) {
      var more = event.target.closest('[data-discovery-more]');
      if (more) {
        if (discoveryLoading) return;
        discoveryFocusIndex = document.querySelector('[data-discovery-grid]').children.length;
        if (!discoveryError) discoveryLimit += 24;
        if (discoveryLimit > discoveryProducts.length && discoveryHasMore) loadDiscovery();
        else renderDiscovery();
        return;
      }
      var button = event.target.closest('[data-avora-upsell-add]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      quickAdd(button);
    });

    document.addEventListener('cart:update', function (event) {
      var cart = event && event.detail && (event.detail.cart || event.detail.data && event.detail.data.cart);
      window.setTimeout(function () {
        if (cart && Number.isFinite(Number(cart.total_price))) renderAll(cart);
        else fetchCartAndRender();
      }, 90);
    });

    document.addEventListener('cart:refresh', fetchCartAndRender);
    document.addEventListener('avora:reward-state', function(event) { if(event.detail && event.detail.cart)renderAll(event.detail.cart); });
    document.addEventListener('avora:rewards-config-ready', function () {
      if (currentCart) renderAll(currentCart);
    });
    fetchCartAndRender();
  }

  window.AvoraCartUpsell = {
    getCurrentCart: function () { return currentCart; },
    whenIdle: function () { return quickAddPromise; },
    rankCandidates: rankCandidates,
    refresh: fetchCartAndRender,
    update: renderAll
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
